import { Page, DatePicker as UpdateDatePicker } from 'components/common';
import React, { useState, useEffect, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { NotificationManager } from 'react-notifications';
import { Col, Row, Modal, ModalHeader, ModalFooter, Button, Table, Form, Input as UpdateInput, FormText, Label } from 'reactstrap';
import { FormGroup as UpdateFormGroup } from '../../Settings/EmailTemplates/FormGroup';
import BackButton from 'components/refactoring/BackButton';
import { useOrganisationContext, useUserContext } from "hooks";
import { ApiClient, } from "services";
import { Control, LocalForm } from 'react-redux-form';
import {
  useForm,
  FormProvider,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import {
  FormGroup,
  Input,
  DatePicker
} from "components/react-hook-form";
import InputFilesForm from 'components/common/InputFilesForm';
import { formatNumber, handleFilesCountUpdate } from 'services/util';

const ReportSummary = ({ t, match }) => {
  const { selectedOrganisation } = useOrganisationContext();
  const { user } = useUserContext();
  const client = ApiClient();

  const auditor_permission = useMemo(() => {
    return user?.permissions?.includes("access_audit_report");;
  }, [user?.permissions]);

  const methods = useForm({
    mode: "onBlur"
  });
  const { reset, control, setValue, handleSubmit, watch, getValues } = methods;


  const { fields, remove: remove1, append: append1 } = useFieldArray({
    control,
    name: "account_file",
    keyName: "key",
  });

  const { fields: fields2, remove: remove2, append: append2 } = useFieldArray({
    control,
    name: "listing_file",
    keyName: "key",
  });

  const count_progress = useWatch({
    control,
    name: "account_file",
  });

  const connectedOrg = selectedOrganisation ? selectedOrganisation?.id : 0;
  const year_id = match.params.id;
  const summary_id = match.params.summary_id;

  const [auditDateRange, setAuditDateRange] = useState();
  const [masterFileData, setMasterFileData] = useState();
  const [auditRequirementsData, setAuditRequirements] = useState();
  const [requestDuringAudit, setRequestDuringAudit] = useState();
  const [uploadedFilesCount, setUploadedFilesCount] = useState([]);


  const progress = useMemo(
    () => (formatNumber((count_progress?.filter(item => item?.audit_requirements_account.length > 0)?.length / count_progress?.length) * 100, "0.0")) || 0,
    [count_progress]
  );

  useEffect(() => {
    addRow();
  }, []);

  useEffect(() => {
    fetchData();
    fetchMasterFileData();
    fetchAuditRequirementsData();
    fetchReqDuringAuditData();
    addListingRow();
    fetchAllFilesData();
  }, []);

  const addListingRow = () => {
    append2({
      listing_name: '',
      uploaded: false,
      listing_upload_date: null,
      upload_file: null,
      listing_deadline: null
    }
    )
  };
  const addRow = () => {
    append1(
      {
        id: null,
        financial_statement_name: '',
        uploaded: false,
        upload_date: '',
        upload_file: null,
        statement_deadline: null
      }
    )
  };

  const fetchData = async () => {

    try {
      const auditDateRange = await client.get(`/audit_reports/${connectedOrg}/date_range/${year_id}`);
      setAuditDateRange(auditDateRange.data);
    } catch (error) {
      NotificationManager.error(error?.response?.data?.error || t("Something went wrong"));
    }
  };


  const fetchMasterFileData = async () => {

    try {
      const auditDateRange = await client.get(`/audit_reports/${connectedOrg}/master_files/${year_id}`);
      setMasterFileData(auditDateRange.data);
    } catch (error) {
      NotificationManager.error(error?.response?.data?.error || t("Something went wrong"));
    }
  };

  const fetchAuditRequirementsData = async () => {

    try {
      const data = await client.get(`/audit_reports/${connectedOrg}/audit-requirements/${year_id}`);
      setAuditRequirements(data.data);
    } catch (error) {
      NotificationManager.error(error?.response?.data?.error || t("Something went wrong"));
    }
  };

  const fetchReqDuringAuditData = async () => {

    try {
      const data = await client.get(`/audit_reports/${connectedOrg}/during_audit/${year_id}`);
      setRequestDuringAudit(data.data);
    } catch (error) {
      NotificationManager.error(error?.response?.data?.error || t("Something went wrong"));
    }
  };

  const fetchAllFilesData = async () => {
    let files_data = {}

    try {
      if (summary_id !== "new") {
        files_data = await client.get(`/audit_reports/${connectedOrg}/reports_summary/${year_id}`);

        files_data.data?.account_file.map(_account_file =>
          ({ ..._account_file, updated_at: files_data?.data?.updated_at })

        )

        reset(files_data.data);
      }

    } catch (error) {
      NotificationManager.error(error?.response?.data?.error || t("Something went wrong"));
    }
  };

  const handleUpdateDateRange = async (values) => {
    const payload = {
      start_date: values.start_date,
      deadline: values.deadline,
      selected_year: year_id,
      auditor_name: values.auditor_name
    };

    try {
      const response = await client.put(`/audit_reports/${connectedOrg}/date_range/${year_id}/update_date_range`, payload);
      NotificationManager.success(t("Updated successfully"));
    } catch (error) {
      NotificationManager.error(error?.response?.data?.error || t("Something went wrong"));
    }
  };

  const handleSummarySubmit = async (values) => {
    const payload = {
      progress: progress,
      add_year: year_id,
      feedback: values?.feedback,
      account_file: [...values?.account_file, ...values?.listing_file],
    };

    try {
      const response = await client.post(`/audit_reports/${connectedOrg}/reports_summary/${year_id}/create_reports_summary`, payload);
      const id = response.data.id
      window.location.replace(`/reports/audit_reports/report_summary/${year_id}/${id}`)
      NotificationManager.success(t("Saved successfully"));
    } catch (error) {
      NotificationManager.error(error?.response?.data?.error || t("Something went wrong"));
    }
  };

  return (
    <Page title={t("Reports summary")}>
      <div className='d-flex justify-content-between align-items-center'>
        <BackButton />
      </div>
      <div>
        {auditDateRange &&
          <>
            <LocalForm initialState={auditDateRange} onSubmit={handleUpdateDateRange}>
              <div className='d-flex justify-content-start align-items-end mr-3'>
                <div>
                  <UpdateFormGroup label="Company Name" className='mr-3'>
                    <Control.text
                      model=".connectedsation"
                      className="form-control"
                      style={{ backgroundColor: '#F4F7FE' }}
                      readOnly
                      defaultValue={selectedOrganisation?.name}
                    />
                  </UpdateFormGroup>
                </div>
                <div>
                </div>
                <div>
                  <UpdateFormGroup label="Auditor Name" className='mr-3'>
                    <Control.text
                      type="text"
                      className="form-control"
                      model=".auditor_name"
                      placeholder={t("Auditor name")}
                      defaultValue={auditDateRange.auditor_name}
                      disabled={auditor_permission === false}
                    />
                  </UpdateFormGroup>
                </div>
                <div className='form-group'>
                  <Button disabled={auditor_permission === false} className='' color="primary" type="submit"><Trans>Save</Trans></Button>
                </div>
              </div>
            </LocalForm>
            <div className='my-1 d-flex justify-content-start align-items-center'>
              <div className='mt-2'>
                <Label><strong><Trans>Audit period end date</Trans></strong></Label>
              </div>
              <div className='ml-2'>
                {auditDateRange?.end_date}
              </div>
            </div>
          </>
        }
        {progress > 0 ? (
          <div className="progress my-3 h-50">
            <div className="progress-bar h-50  progress-bg-clr" role="progressbar" style={{ width: `${progress}%` }}>
              {progress}%
            </div>
          </div>
        ) :
          <div className="progress my-3 h-50">
            <div className="progress-bar h-50  progress-bg-clr" role="progressbar">
              {0}%
            </div>
          </div>
        }
        <FormProvider {...methods}>
          <div className=" border ">
            <div className="my-4 ml-2 mr-2 ">
              <Table>
                <thead>
                  <tr>
                    {/* <th>#</th> */}
                    <th className="text-center"><Trans>Financial statement</Trans></th>
                    <th className="text-center"><Trans>Valid</Trans></th>
                    <th className="text-center"><Trans>Upload</Trans></th>
                    <th className="text-center"><Trans>Due date</Trans></th>
                    <th className="text-center"><Trans>Actions</Trans></th>
                    <th className="text-right" />
                  </tr>
                </thead>
                <tbody>
                  {fields.map((row, index) => {
                    const n = (v) => `account_file.${index}.${v}`;
                    console.log(row, 'field');

                    return (
                      <tr key={row?.key}>
                        {/* <td scope="row">{row?.id}</td> */}
                        <td className="text-center">
                          <Input
                            type="hidden"
                            name={n("id")}
                            value={row?.id}
                          />
                          <FormGroup>
                            <Input
                              type="text"
                              className="form-control"
                              name={n("financial_statement_name")}
                              placeholder={t('Financial statement')}
                            />
                          </FormGroup>
                        </td>
                        <td className="text-center">
                          <FormGroup className="d-flex justify-content-center align-items-center">
                            <Input
                              type='checkbox'
                              name={n("is_required")}
                              checked={getValues(n("is_required"))}
                              disabled={auditor_permission === false}
                            />
                          </FormGroup>
                        </td>
                        <td className="text-center">
                          <FormGroup>
                            <InputFilesForm t={t} getData={getValues} name={n(`audit_requirements_account`)} setValue={setValue} indx={index} control={control} onFilesCountUpdate={(count) => handleFilesCountUpdate(count, setUploadedFilesCount, index)} uploadedFilesCount={uploadedFilesCount[index] || 0} auditor_permission={auditor_permission}></InputFilesForm>
                          </FormGroup>
                        </td>
                        <td className="text-center">
                          <FormGroup>
                            <DatePicker
                              type="date"
                              className="form-control"
                              name={n("statement_deadline")}
                              placeholder="Due Date"
                            />
                          </FormGroup>
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center align-items-center">
                            <Button color="danger" onClick={() => remove1(index)}><i className='fa fa-trash'></i></Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
              <Button className='mb-2' color="success" onClick={addRow}>+ <Trans>Add new statement</Trans></Button>
            </div>
          </div>
          <div className=" border mt-2">
            <div className="my-4 ml-2 mr-2 ">
              <Table>
                <thead>
                  <tr>
                    {/* <th>#</th> */}
                    <th className="text-center"><Trans>Uploading lists</Trans></th>
                    <th className="text-center"><Trans>Status</Trans></th>
                    <th className="text-center"><Trans>Upload date</Trans></th>
                    <th className="text-center"><Trans>Due date</Trans></th>
                    <th className="text-center"><Trans>Actions</Trans></th>
                    <th className="text-right" />
                  </tr>
                </thead>
                <tbody>
                  {fields2.map((row, index) => {
                    const n = (v) => `listing_file.${index}.${v}`;
                    return (
                      <tr key={row.key}>
                        {/* <td scope="row">{row.id}</td> */}
                        <td className="text-center">
                          <FormGroup>
                            <Input
                              type="text"
                              className="form-control"
                              name={n("listing_name")}
                              placeholder={t('Uploading lists')}
                            />
                          </FormGroup>
                        </td>
                        <td className="text-right">
                          <select className='p-2'>
                            <option value="select">{t('select')}</option>
                            <option value="uploaded">{t('Uploaded')}</option>
                            <option value="Not Uploaded">{t('Not Uploaded')}</option>
                          </select>
                        </td>
                        <td className="text-center">
                          <FormGroup>
                            <DatePicker
                              type="date"
                              className="form-control"
                              name={n("listing_upload_date")}
                            />
                          </FormGroup>
                        </td>
                        <td className="text-center">
                          <FormGroup>
                            <DatePicker
                              type="date"
                              className="form-control"
                              name={n("listing_deadline")}
                            />
                          </FormGroup>
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center align-items-center">
                            <Button color="danger" onClick={() => remove2(index)}><i className='fa fa-trash'></i></Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
              <Button color="success" onClick={addListingRow}>+ <Trans>Add new listing</Trans></Button>
            </div>
          </div>
          <div className=" border mt-2">
            <div className="my-4 ml-2 mr-2">
              <Row className="align-items-center mt-2">
                <Col md="4">
                  <span>Master Files</span>
                </Col>
                <Col md="8">
                  {masterFileData?.account_file?.length > 0 && masterFileData?.progress > 0 ? (
                    <div className="progress my-3 h-50">
                      <div className="progress-bar h-50 text-white progress-bg-clr" role="progressbar" style={{ width: `${masterFileData?.progress}%` }}>
                        {masterFileData?.progress || 0}%
                      </div>
                    </div>
                  ) :
                    <div className="progress my-3 h-50">
                      <div className="progress-bar h-50 text-white progress-bg-clr" role="progressbar">
                        {0}%
                      </div>
                    </div>
                  }
                </Col>
              </Row>

              <Row className="align-items-center">
                <Col md="4">
                  <span><Trans>Audit Requirements</Trans></span>
                </Col>
                <Col md="8">
                  {auditRequirementsData?.account_file?.length > 0 && auditRequirementsData?.progress > 0 ? (
                    <div className="progress my-3 h-50">
                      <div className="progress-bar h-50 text-white progress-bg-clr" role="progressbar" style={{ width: `${auditRequirementsData?.progress}%` }}>
                        {`${auditRequirementsData?.progress}%`}
                      </div>
                    </div>
                  ) : (
                    <div className="progress my-3 h-50">
                      <div className="progress-bar h-50 text-white progress-bg-clr" role="progressbar">
                        {0}%
                      </div>
                    </div>
                  )}
                </Col>
              </Row>

              <Row className="align-items-center">
                <Col md="4">
                  <span><Trans>Request during audit</Trans></span>
                </Col>
                <Col md="8">
                  {requestDuringAudit?.account_file?.length > 0 && requestDuringAudit?.progress > 0 ? (
                    <div className="progress my-3 h-50">
                      <div className="progress-bar h-50 text-white progress-bg-clr" role="progressbar" style={{ width: `${requestDuringAudit?.progress}%` }}>
                        {requestDuringAudit?.progress || 0}%
                      </div>
                    </div>
                  ) :
                    <div className="progress my-3 h-50">
                      <div className="progress-bar h-50 text-white progress-bg-clr" role="progressbar">
                        {0}%
                      </div>
                    </div>
                  }
                </Col>
              </Row>

              <Row>
                <Col md="12">
                  <FormGroup>
                    <Input
                      type="text"
                      className="form-control"
                      name={("feedback")}
                      placeholder="Feedback"
                    />
                  </FormGroup>
                </Col>
              </Row>
            </div>
          </div>
          <Row>
            <Col md="12">
              <FormGroup className="text-right">
                <Button className='mt-2 ml-auto' type="submit" color='primary' onClick={handleSubmit(handleSummarySubmit)}><Trans>Submit</Trans></Button>
              </FormGroup>
            </Col>
          </Row>
        </FormProvider>

      </div>
    </Page>
  );
};

export default ReportSummary;
